/**
 * Main Layout component for the application.
 * Wraps pages with consistent navigation and structure.
 */

import React, { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, LayoutDashboard, Bot, CreditCard, Puzzle, Settings, Home, FileText, TrendingUp, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useVertical } from '@/hooks/useVertical';
import { useNavigation } from '@/hooks/useNavigation';
import { ThemeToggle } from '@/contexts/ThemeContext';

import { VersionDisplay } from '@/components/VersionDisplay';

interface LayoutProps {
  children: ReactNode;
}

// Icon mapping for navigation items
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Bot,
  CreditCard,
  Puzzle,
  Settings,
  Home,
  FileText,
  TrendingUp,
  Shield,
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { vertical } = useVertical();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { navigationItems, isActivePath } = useNavigation();

  // Split navigation items into main and user menu sections
  const mainNavItems = navigationItems.filter(item => item.position !== 'user-menu');
  const userMenuNavItems = navigationItems.filter(item => item.position === 'user-menu');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Top Navigation Bar */}
      <nav
        className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/dashboard" className="flex items-center space-x-2">
                  <img src="/v4_3.png" alt="ClawFarm logo" className="h-8 w-auto" />
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    ClawFarm
                  </span>
                </Link>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                {mainNavItems.map((item) => {
                  const IconComponent = iconMap[item.icon];
                  return (
                    <Link
                      key={item.id}
                      to={item.href}
                      title={item.label}
                      className={`
                        inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors
                        ${isActivePath(item.href)
                          ? 'border-primary-500 text-gray-900 dark:text-white'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-300'
                        }
                      `}
                      aria-current={isActivePath(item.href) ? 'page' : undefined}
                    >
                      {IconComponent && <IconComponent className="w-4 h-4 mr-1" aria-hidden="true" />}
                      <span className="hidden md:inline">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center sm:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                aria-controls="mobile-menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open main menu'}</span>
                <Menu className="block h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* User menu */}
            <div className="hidden sm:flex items-center space-x-4 overflow-x-auto min-w-0">
              <ThemeToggle aria-label="Toggle dark mode" />
              {userMenuNavItems.map((item) => {
                const IconComponent = iconMap[item.icon];
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    title={item.label}
                    className={`
                      inline-flex items-center px-3 py-2 text-sm font-medium transition-colors
                      ${isActivePath(item.href)
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }
                    `}
                    aria-current={isActivePath(item.href) ? 'page' : undefined}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4 mr-1" aria-hidden="true" />}
                    <span className="hidden md:inline">{item.label}</span>
                  </Link>
                );
              })}
              {user && (
                <>
                  <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[100px] truncate">
                    {user.full_name}
                  </span>
                  <button
                    onClick={logout}
                    className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    aria-label="Logout"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden" id="mobile-menu">
            <div className="pt-2 pb-3 space-y-1">
              {mainNavItems.map((item) => {
                const IconComponent = iconMap[item.icon];
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      block pl-3 pr-4 py-2 border-l-4 text-base font-medium
                      ${isActivePath(item.href)
                        ? 'border-primary-500 text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4 mr-2 inline" aria-hidden="true" />}
                    {item.label}
                  </Link>
                );
              })}
              {userMenuNavItems.map((item) => {
                const IconComponent = iconMap[item.icon];
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      block pl-3 pr-4 py-2 border-l-4 text-base font-medium
                      ${isActivePath(item.href)
                        ? 'border-primary-500 text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4 mr-2 inline" aria-hidden="true" />}
                    {item.label}
                  </Link>
                );
              })}
              {user && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="px-4 pb-2 text-sm text-gray-700 dark:text-gray-300">
                    {user.full_name}
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left pl-3 pr-4 py-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            &copy; 2026 {vertical.displayName}. {vertical.description}
          </p>
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">
            <VersionDisplay />
          </p>
        </div>
      </footer>
    </div>
  );
};
